import React from "react";
import {observable, action} from "mobx";
import {Modal, message} from "antd";
import {QuestionCircleOutlined} from "@ant-design/icons";

import constantService from "../services/constantService";
import {
    routes,
    customerRoutes,
    nonApiIntegratedCustomerRoutes,
    poolCustomerRoutes,
    poolAndUatCustomerRoutes
} from "../routes/routes";
import meStore from "./meStore";
import i18n from "../i18n";

class appStore {
    @observable loading = false;
    @observable companyUserList = [0];
    @observable companyListFilter = [];
    @observable currentPath = {};
    @observable beforeLogin = "";
    @observable showError = true;

    @action setCurrentPath = path => {
        const determineRoutes = () => {
            if (meStore.checkRole("isCompanyUser")) {
                if (meStore.checkRole("isPoolCustomer") && meStore.checkRole("isUatCustomer")) {
                    return poolAndUatCustomerRoutes;
                } else if (meStore.checkRole("isPoolCustomer")) {
                    return poolCustomerRoutes;
                } else if (meStore.checkRole("isNonApiIntegratedCustomer")) {
                    return nonApiIntegratedCustomerRoutes;
                } else {
                    return customerRoutes;
                }
            } else {
                return routes;
            }
        };

        const availableRoutes = determineRoutes();
        this.currentPath = availableRoutes.find(route => path.startsWith(route.path));
    };

    @action setBeforeLogin = path => {
        this.beforeLogin = path;
    };

    @action setLoading = setValue => (this.loading = setValue);

    @action openErrorModal = ({
        title = i18n.t("modal.errorTitle", {ns: "common"}),
        content,
        onOk = () => {},
        okText = i18n.t("modal.okButton", {ns: "common"}),
        width,
        zIndex = 9999
    }) => {
        if (this.showError) {
            this.showError = false;
            return Modal.error({
                title,
                content,
                okButtonProps: {"data-testid": "errorModal_okButton"},
                onOk: () => {
                    onOk();
                    this.showError = true;
                },
                okText,
                width,
                zIndex
            });
        }
    };
    @action openSuccessModal = ({title = "", content, onOk, okText = "OK"}) =>
        Modal.success({title, content, onOk, style: {whiteSpace: "break-spaces"}, okText});

    @action openWarningModal = ({title = "", content, onOk}) =>
        Modal.warning({title, content, onOk, style: {whiteSpace: "break-spaces"}});

    @action openConfirmModal = ({
        title = "",
        content,
        onOk,
        okText = i18n.t("modal.yesButton", {ns: "common"}),
        onCancel = () => {},
        cancelText = i18n.t("modal.noButton", {ns: "common"}),
        okButtonProps,
        width
    }) =>
        Modal.confirm({
            title,
            content,
            onOk,
            okText,
            onCancel,
            cancelText,
            okButtonProps,
            width,
            icon: <QuestionCircleOutlined />
        });

    @action openInfoModal = ({
        title = i18n.t("modal.infoTitle", {ns: "common"}),
        content,
        onOk = () => {},
        okText = i18n.t("modal.okButton", {ns: "common"})
    }) => {
        return Modal.info({
            title,
            content,
            okButtonProps: {"data-testid": "infoModal_okButton"},
            onOk: () => {
                onOk();
            },
            okText
        });
    };

    @action showSuccessMessage = ({content, duration = 1.5}) =>
        message.success({content, duration});

    @action getCompanyListFilter = async () => {
        this.loading = true;
        const resultOfRequest = await constantService.getCompanyListFilter();
        if (resultOfRequest.status !== 200) {
            this.openErrorModal({content: resultOfRequest.data.message});
            this.loading = false;
            return false;
        } else {
            this.loading = false;
            this.companyListFilter = await resultOfRequest.data;
        }
    };

    @action getCompanyUserList = async () => {
        this.loading = true;
        const resultOfRequest = await constantService.getCompanyUserList();
        if (resultOfRequest.status !== 200) {
            this.openErrorModal({content: resultOfRequest.data.message});
            this.loading = false;
            return false;
        } else {
            this.loading = false;
            this.companyUserList = await resultOfRequest.data;
        }
    };

    @action resetAppStoreData = async () => {
        this.companyUserList = [0];
        this.companyListFilter = [];
        this.currentPath = {};
        this.beforeLogin = "";
    };
}

export default new appStore();
